import type { EventBus } from '../EventBus';
import type { PlatformAdapter } from '../adapters/PlatformAdapter';
import type { LocalStorageService } from './browser/LocalStorageService';
import type { TrackingDebugService } from './TrackingDebugService';
import { StorageKey } from '../config/storageKeys';
import { TrackingBusEvent } from '../constants/trackingBusEvent';
import { TrackingTag } from '../constants/trackingTag';
import { readMailSeed } from '../mailSeed';

interface CustomerResolvedPayload {
  customerId: string | null;
}

/**
 * Resolves the logged-in customer id from the **platform adapter** (Shopware: JSON route) and mirrors it in
 * localStorage. Uses **`emitCrossTab`** so every open tab updates without a second HTTP round-trip when the
 * first tab finishes resolution.
 */
/**
 * One observation of who the visitor is, and on what evidence. `source` is absent exactly when `id`
 * is null — INT-296 reads an absent provenance as `'login'`, so a source next to a null id would be
 * meaningless. Named rather than inline because both payload build sites and the wire type refer to
 * it, and all four Shopware version repos carry a copy.
 */
export interface CustomerIdentity {
  id: string | null;
  source?: 'login' | 'mail';
}

export class CustomerService {
  private inMemoryCustomerId: string | null = null;

  constructor(
    private readonly storage: LocalStorageService,
    private readonly adapter: PlatformAdapter,
    private readonly bus: EventBus,
    private readonly debug?: TrackingDebugService,
  ) {}

  init(): void {
    const cached = this.storage.get(StorageKey.CustomerId);
    this.inMemoryCustomerId = cached;

    this.bus.subscribe<CustomerResolvedPayload>(
      TrackingBusEvent.CustomerResolved,
      ({ customerId }) => {
        this.inMemoryCustomerId = customerId;
        if (customerId == null) {
          this.storage.remove(StorageKey.CustomerId);
        } else {
          this.storage.set(StorageKey.CustomerId, customerId);
        }
      },
      TrackingTag.CustomerService,
    );

    this.bus.subscribe(
      TrackingBusEvent.ConsentWithdrawn,
      () => {
        this.storage.remove(StorageKey.CustomerId);
        this.storage.remove(StorageKey.MailSeed);
        this.inMemoryCustomerId = null;
      },
      TrackingTag.CustomerService,
    );

    // Async resolve in background (does not block startup).
    void this.resolveCustomerId();
  }

  /**
   * One snapshot of id **and** provenance. Two separate getters could straddle a cross-tab
   * `customer:resolved` and report a `'login'` source next to a seed id. The seed is read live from
   * storage rather than cached at `init()`, so a seed written by the landing tab reaches the beats
   * of tabs that were already open.
   */
  getIdentity(): CustomerIdentity {
    // `!= null` alone is not enough: the adapter passes `{ id: "" }` through unchanged, and an empty
    // string would both mask a valid seed and go out labelled as a verified login.
    if (this.inMemoryCustomerId != null && this.inMemoryCustomerId !== '') {
      return { id: this.inMemoryCustomerId, source: 'login' };
    }

    const seed = readMailSeed(this.storage);
    if (seed != null) {
      return { id: seed, source: 'mail' };
    }

    return { id: null };
  }

  private async resolveCustomerId(): Promise<void> {
    try {
      const customerId = await this.adapter.getCustomerId();
      this.debug?.log('CustomerService', 'resolveCustomerId done', { hasId: customerId != null && customerId !== '' });
      this.bus.emitCrossTab<CustomerResolvedPayload>(TrackingBusEvent.CustomerResolved, { customerId }, { publisher: TrackingTag.CustomerService });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      this.debug?.log('CustomerService', 'resolveCustomerId failed', { message });
    }
  }
}
